import { isEmpty } from '@nirvana/shared/basic-types/object';
import { GzLinkWebComponent } from '../ui/gzLinkComponent';
import { GzIconLabelWebComponent } from '../ui/gzIconLabel';

export enum OverflowDirection {
  HORIZONTAL = 'horizontal',
  VERTICAL = 'vertical',
  ANY = 'any',
}

export function isElementOverflowed(
  element: HTMLElement,
  overflowDirection: OverflowDirection = OverflowDirection.ANY
): boolean {
  if (!element) {
    return false;
  }

  switch (overflowDirection) {
    case OverflowDirection.HORIZONTAL:
      return element.scrollWidth > element.clientWidth;
    case OverflowDirection.VERTICAL:
      return element.scrollHeight > element.clientHeight;
    default:
      return element.scrollWidth > element.clientWidth || element.scrollHeight > element.clientHeight;
  }
}

export function showElementIfTargetOverflows(
  target: HTMLElement,
  element: HTMLElement,
  overflowDirection: OverflowDirection = OverflowDirection.ANY
) {
  if (element.innerText === '' || isEmpty(element.innerHTML)) {
    return;
  }

  const isOverflowed = target && isElementOverflowed(target, overflowDirection);
  if (!isOverflowed) {
    return;
  }

  if (!element.classList.contains('visible')) {
    element.classList.add('visible');
  }
}

export function alignElementToMousePointer(event: MouseEvent, element: HTMLElement) {
  // get horizontal and vertical coordinates of the mouse pointer
  const { clientX, clientY } = event;

  // get browser window width & height
  const windowWidth = window.innerWidth;
  const windowHeight = window.innerHeight;

  // get available space both vertical & horizontal to show tooltipBox
  const mouseHorizontalPos = windowWidth - clientX;
  const mouseVerticalPos = windowHeight - clientY;

  // get box width and height
  const boxCoords = element.getBoundingClientRect();
  const boxWidth = boxCoords.width;
  const boxHeight = boxCoords.height;

  // add a bit of vertical & horizontal space between the tooltip and its anchor
  const offsetTop = 10;
  const offsetLeft = 10;

  /**
   * position the tooltip based on the available space in the window and
   * the mouse pointer
   */
  element.style.left =
    mouseHorizontalPos <= boxWidth + offsetLeft * 2
      ? clientX - (boxWidth + offsetLeft) + 'px'
      : clientX + offsetLeft + 'px';
  element.style.top =
    mouseVerticalPos <= boxHeight + offsetTop * 2 ? clientY - boxHeight + 'px' : clientY + offsetTop + 'px';
}

export function showElement(element: HTMLElement) {
  if (element && !element.classList.contains('visible')) {
    element.classList.add('visible');
  }
}

export function hideElement(element: HTMLElement) {
  if (element && element.classList.contains('visible')) {
    element.classList.remove('visible');
  }
}

/**
 * Returns true if a click event occurred outside the element, and false otherwise.
 */
export function isMouseEventOutsideElement(event: MouseEvent, instance: HTMLElement): boolean {
  // For mouse events, if the composed path does NOT include the instance, the event was outside the element.
  return !composedPathContainsElement(event, instance);
}

/**
 * Returns true if a click event occurred outside the element, and false otherwise by tagName.
 */
export function isMouseEventOutsideElementByTagName(event: MouseEvent, tagName: string | null): boolean {
  const path = event.composedPath() || [];
  const composedPathContainsElement = !isEmpty(path.find((el: HTMLElement) => el.localName === tagName));

  return !composedPathContainsElement;
}

/**
 * Returns true if a blur event occurred outside the element, and false otherwise.
 */
export function isBlurEventOutsideElement(event: FocusEvent, instance: HTMLElement): boolean {
  if (event.type !== 'blur') {
    throw new Error('Unexpectedly called isBlurEventOutsideElement with a non-blur event');
  }

  // As opposed to mouse events, blur events happening outside the given element contain the element.
  return composedPathContainsElement(event, instance);
}

function composedPathContainsElement(event: UIEvent, instance: HTMLElement) {
  const path = event.composedPath() || [];
  return !isEmpty(path.find(el => el === instance));
}

export enum ElementClass {
  HIDDEN = 'hidden',
  VISIBLE = 'visible',
  HOVER = 'hover',
  FILTER = 'filter',
  INCIDENTS_NO_LIST_CONTENT = 'no-list-content',
}

export function elementClassListToggle(element: HTMLElement, className: string) {
  if (!element.classList.contains(className)) {
    element.classList.add(className);
  } else {
    element.classList.remove(className);
  }
}

export function elementClassListAdd(element: HTMLElement, className: string) {
  if (!element.classList.contains(className)) {
    element.classList.add(className);
  }
}

export function elementClassListRemove(element: HTMLElement, className: string) {
  element.classList.remove(className);
}

export function elementClassListUpdate(element: HTMLElement, classMap: Record<string, boolean>) {
  for (const [className, shouldApply] of Object.entries(classMap)) {
    shouldApply ? elementClassListAdd(element, className) : elementClassListRemove(element, className);
  }
}

export function toggleVisibility(element: HTMLElement, isVisible: boolean) {
  element.classList.toggle('hidden', !isVisible);
}

export function toggleActive(element: HTMLElement, isActive: boolean) {
  if (isActive) {
    element.classList.add('active');
  } else {
    element.classList.remove('active');
  }
}

export function toggleDisabled(element: HTMLElement, isDisabled: boolean) {
  if (isDisabled) {
    elementClassListAdd(element, 'disabled');
  } else {
    elementClassListRemove(element, 'disabled');
  }
}

export function isHidden(element: HTMLElement) {
  return element.classList.contains('hidden');
}

export function isVisible(element: HTMLElement): boolean {
  return element.style.display !== 'none' && element.style.visibility !== 'hidden';
}

export function getRgbByValue(score: number): string {
  //the maximum allowed range for the rgb 193 is the yellow with a little orange
  const maxRange = 193;
  const colorValue = Math.floor(maxRange - ((score - 10) / (100 - 10)) * maxRange);
  return 'rgb(255, ' + colorValue + ', 0)';
}

export function insertAfter(newNode: HTMLElement, existingNode: HTMLElement): void {
  existingNode.parentNode?.insertBefore(newNode, existingNode.nextSibling);
}

export function copyToClipboard(inputValue: string | number, textareaType?: boolean | undefined): void {
  let temporaryField;

  if (textareaType) {
    temporaryField = document.createElement('textarea');
  } else {
    temporaryField = document.createElement('input');
  }

  temporaryField.style.opacity = '0';
  temporaryField.value = inputValue.toString();

  document.body.appendChild(temporaryField);

  const copyText = temporaryField;

  if (copyText) {
    copyText.select();
    copyText.setSelectionRange(0, 99999);

    document.execCommand('copy');
  }

  temporaryField.remove();
}

export function disableDraggingOnElements(elementsList: NodeListOf<HTMLElement>) {
  elementsList.forEach(element => element.setAttribute('draggable', 'false'));
}

export function isTargetActionable(target: EventTarget): boolean {
  return (
    target instanceof HTMLButtonElement ||
    target instanceof HTMLInputElement ||
    target instanceof HTMLLabelElement ||
    target instanceof HTMLAnchorElement ||
    target instanceof GzLinkWebComponent ||
    target instanceof GzIconLabelWebComponent
  );
}

export function isScrollable(element) {
  return element && element.scrollHeight > element.clientHeight;
}

export function emphasizeString(filter: string, item: string): string {
  if (filter === '') {
    return item;
  }
  filter = filter.replace(/[/\-\\^$*+?.()|[\]{}]/g, '\\$&').replace(/\s+/g, '|');

  return item.replace(new RegExp('(' + filter + ')', 'ig'), '<b>$1</b>');
}

export function emphasizeFirstOccurrence(filter: string, item: string): string {
  if (filter === '') {
    return item;
  }

  filter = filter.replace(/[/\-\\^$*+?.()|[\]{}]/g, '\\$&').replace(/\s+/g, '|');
  filter = filter.replace(/\\\*/g, '.*?');

  const regex = new RegExp('(' + filter + ')', 'i');
  return item.replace(regex, '<b>$1</b>');
}

export function setElementClass(element, className): void {
  if (!element.classList.contains(className)) {
    element.classList.add(className);
  }
}

export function removeElementClass(element, className): void {
  element.classList.remove(className);
}

/**
 * Queries for an element by walking through a list of selectors,
 * potentially descending into shadow roots along the way.
 * It searches only in the shadowRoot of found elements.
 *
 * @param root - The starting root
 * @param selectors - An ordered array of selectors (as strings)
 * @returns The matched Element, or null if not found.
 *
 * Usage example:
 *   const icon = queryShadowHierarchy(rootElement, [
 *     'gz-accordion',
 *     'gz-icon.dropdown-icon'
 *   ]);
 */
export function queryShadowHierarchy(root: Element, selectors: string[]): Element | null {
  // Our "context" may become an Element (and potentially its shadowRoot),
  // so we'll store it in a variable that can handle both.
  let currentContext: Element = root;

  for (let i = 0; i < selectors.length; i++) {
    const selector = selectors[i];

    // Make sure this context supports querySelector
    if (typeof currentContext.shadowRoot?.querySelector !== 'function') {
      return null;
    }

    // Query within the current context
    const found = currentContext.shadowRoot?.querySelector(selector);
    if (!found) {
      return null;
    }

    // If we have more selectors ahead, attempt to descend
    // into the shadowRoot if it's open
    const hasMoreSteps = i < selectors.length - 1;
    if (hasMoreSteps && found instanceof HTMLElement && found.shadowRoot) {
      currentContext = found;
    } else {
      // Otherwise, this becomes our new context
      currentContext = found;
    }
  }

  // By the end, currentContext should be the final Element we found
  return currentContext as Element;
}
